import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BaseComponent } from '../base-component';
import { EventService } from '../service/event.service';
import { ChangeNavigationEvent, ProgressBarEvent, ValidateAndNavigateEvent, SkipToMainContentEvent } from '../service/events';
import { AuthService } from '../service/auth.service';
import { AFOtherBgcInfo } from '../model/af.model';
import { UntypedFormGroup, Validators, UntypedFormBuilder, ValidatorFn, ValidationErrors } from '@angular/forms';
import { ConsentService } from '../service/consent.service';
import { DatePipe } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomValidatorsService } from '../service/custom-validators.service';

@Component({
  selector: 'app-other-background-check',
  templateUrl: './other-background-check.component.html',
  styleUrls: ['./other-background-check.component.scss'],
  standalone: false
})
export class OtherBackgroundCheckComponent extends BaseComponent implements OnInit, OnDestroy {

  afOtherBgcInfo: AFOtherBgcInfo;
  otherData: UntypedFormGroup;
  signatureImg: string; //for @Input wet-signature
  date: any;
  verifyId: string;
  afId: string;
  check: boolean = false;
  sigCheckValue: boolean = false;

  regexName: RegExp = /^[A-Za-z\u00C0-\u017F\-',. ]+$/;

  constructor(private readonly eventBus: EventService, private readonly fb: UntypedFormBuilder, private readonly _router: Router, private readonly userService: AuthService, private readonly consentService: ConsentService,
    private readonly datepipe: DatePipe, private readonly _snackBar: MatSnackBar, private readonly customValidator: CustomValidatorsService) {
    super(eventBus, _router)
    this.verifyId = this.userService.getVerifyId();
  }

  get output() { return this.otherData.get('output') };
  get otherBgcLegalRequirementsMet() { return this.otherData.get('otherBgcLegalRequirementsMet') };
  get otherBgcApplicantSignedName() { return this.otherData.get('otherBgcApplicantSignedName') };
  get isCAStateDisclosure() { return this.otherData.get('isCAStateDisclosure') };
  get isOKStateDisclosure() { return this.otherData.get('isOKStateDisclosure') };
  get isMNStateDisclosure() { return this.otherData.get('isMNStateDisclosure') };
  get bookmarkUrl() { return this.otherData.get('bookmarkUrl') };

  ngOnInit() {
    this.createGroup();
    this.subscribeEvents();
  }

  ngAfterViewInit() {
    this.otherData.valueChanges.subscribe(() => {
      setTimeout(() => {
        let progress = this.getPercentage(this.otherData);
        this.eventBus.emit(new ProgressBarEvent({ id: progress }));
      }, 100);
    });

    // retain navigation on page refresh
    let navlink = this.userService.getNavLinks().find(e => e.routerLink === "/otherBackgroundCheck");
    this.eventBus.emit(new ChangeNavigationEvent({ id: navlink.id }));
  }

  createGroup() {
    this.otherData = this.fb.group({
      output: ['', Validators.required],
      otherBgcLegalRequirementsMet: ['', Validators.required],
      otherBgcApplicantSignedName: ['', [Validators.required, this.customValidator.validateRequiredField, Validators.maxLength(250), Validators.pattern(this.regexName)]],
      isCAStateDisclosure: [''],
      isOKStateDisclosure: [''],
      isMNStateDisclosure: [''],
      bookmarkUrl: ['']
    },
      {
        validators: otherValidator()
      });
  }

  subscribeEvents() {
    this.consentService.getOtherBgcInfo().subscribe({
      next: (data: AFOtherBgcInfo) => {
        this.afOtherBgcInfo = data;
        this.otherData.patchValue(this.afOtherBgcInfo);
        this.signatureImg = this.output.value;
        this.afId = this.afOtherBgcInfo.afId;
        this.date = this.afOtherBgcInfo.otherBgcLegalRequirementCheckDate;
      }
    });

    this.otherBgcLegalRequirementsMet.valueChanges.subscribe(() => {
      this.setDate();
    });

    this.sub = this.eventBus.on(
      ValidateAndNavigateEvent,
      (event): void => {
        if (event.payload.currentLink === '/otherBackgroundCheck') {
          this.bookmarkUrl.setValue("");
          if (event.payload.save) {
            this.submitOtherBgcInfo((this.otherData.value), event);
          } else if (event.payload.saveAndFinishLater) {
            this.bookmarkUrl.setValue(event.payload.currentLink);
            this.submitOtherBgcInfo((this.otherData.value), event);
          } else if (this.validate()) {
            this.submitOtherBgcInfo((this.otherData.value), event);
          }
        }
      }
    );

    this.eventBus.on(
      SkipToMainContentEvent,
      (event): void => {
        if (event.payload.text && event.payload.text == "/otherBackgroundCheck") {
          let mainContentElement = document.querySelector("#main-content");//check if main-content id exists on page
          if (mainContentElement) {
            mainContentElement.scrollIntoView(true);
          }
        }
      }
    );

    this.eventBus.emit(new ProgressBarEvent({ id: 0 }));
  }

  onFocusOutMainContent(event) {
    this.eventBus.emit(new SkipToMainContentEvent({ text: "FocusOff" }));
  }

  submitOtherBgcInfo(afOtherBgcInfo: AFOtherBgcInfo, event) {
    afOtherBgcInfo.output = this.signatureImg;
    afOtherBgcInfo.otherBgcLegalRequirementCheckDate = this.date;
    afOtherBgcInfo.otherBgcLegalRequirementCheckDateHidden = this.date;
    afOtherBgcInfo.signatureDate = this.date;
    afOtherBgcInfo.afId = this.afId;
    afOtherBgcInfo.isCAStateDisclosure = this.isCAStateDisclosure.value;
    afOtherBgcInfo.isOKStateDisclosure = this.isOKStateDisclosure.value;
    afOtherBgcInfo.isMNStateDisclosure = this.isMNStateDisclosure.value;

    this.consentService.saveOtherBgcInfo(afOtherBgcInfo).subscribe({
      next: () => {
        if (event.payload.save) {
          this._snackBar.open("Saved successfully", "Close", {
            duration: 4000,
          });
        } else if (event.payload.saveAndFinishLater) {
          this._snackBar.open("Saved successfully", "Close", {
            duration: 4000,
          });
          this._router.navigate(['sessionTimedOut']);
        } else {
          this.eventBus.emit(new ChangeNavigationEvent({ id: event.payload.id }));
          this._router.navigateByUrl(event.payload.nextNavLink);
        }
      },
      error: (error: any) => console.log(error)
    });
  }

  getPercentage(container: UntypedFormGroup): number {
    let errorCount = 0;
    let totalCount = 0;

    for (let controlKey in container.controls) {

      if (container.controls.hasOwnProperty(controlKey)) {
        if (container.controls[controlKey].validator != null) {
          totalCount += container.controls[controlKey].validator.length;
        }
        if (container.controls[controlKey].errors) {
          errorCount += Object.keys(container.controls[controlKey].errors).length;
        }
      }
    }
    let percentage = ((totalCount - errorCount) / totalCount) * 100;
    return percentage;
  }

  getUrl(sig: string) {
    this.signatureImg = sig; // Gets the base64 String from wet-signature @Output
    this.otherData.get('output').setValue(sig);
  }

  sigCheck(sigCheck: boolean) {
    this.sigCheckValue = sigCheck;
  }

  legalOnBlur() {
    if (this.otherBgcLegalRequirementsMet.value) {
      this.check = false;
    } else {
      this.check = true;
    }
  }

  legalOnFocus() {
    this.check = false;
  }

  setDate() {
    if (this.otherBgcLegalRequirementsMet.value) {
      if (!this.date) {
        if (!this.afOtherBgcInfo.otherBgcLegalRequirementCheckDate) {
          this.date = this.datepipe.transform(new Date(), 'MM/dd/yyyy HH:mm:ss');
        }
      }
    } else if (!this.afOtherBgcInfo.otherBgcLegalRequirementCheckDate) {
      this.date = null;
    }
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  validate() {
    this.otherData.markAllAsTouched();
    this.userService.setFocus(this.otherData);
    if (!this.otherBgcLegalRequirementsMet.value) {
      this.otherBgcLegalRequirementsMet.setErrors({ invalid: true });
      this.check = true;
    }

    if (!this.output.value) {
      this.output.setErrors({ invalid: true });
    }

    if (this.otherData.invalid) {
      return false;
    }

    return true;
  }
}

export function otherValidator(): ValidatorFn {
  return (control: UntypedFormGroup): ValidationErrors | null => {
    const errors: ValidationErrors = {};
    const otherBgcLegalRequirementsMetControl = control.get('otherBgcLegalRequirementsMet');

    if (otherBgcLegalRequirementsMetControl.value) {
      otherBgcLegalRequirementsMetControl.setErrors(null);
    } else {
      otherBgcLegalRequirementsMetControl.setErrors({ uncheck: true });
    }

    return Object.keys(errors).length ? errors : null;
  };
}
